/**
 * Untitled Goods — Phase 2 measurement, marketing-site half.
 * Loaded once, deferred (see layout/theme.liquid), never render-blocking.
 *
 * Three jobs, mirroring src/lib/attribution.ts and src/components/AnalyticsBootstrap.tsx in the
 * configurator app exactly (same localStorage key/schema, same capture algorithm, same GA4
 * property) so the visitor's journey stays one continuous, analytically coherent session across
 * the hop from this Shopify-rendered page into the separately-hosted /a/design app:
 *   1. First-party attribution capture (first-touch / latest-touch).
 *   2. GA4 (gtag.js) — GA4's own normal page_view/session behavior is left alone; this never sends
 *      a manual page_view.
 *   3. `design_cta_click` — the one marketing-site funnel event, fired when a "Design Your
 *      Hat"/"Start Designing"-style link into the configurator is actually clicked.
 *
 * The Shopify-managed Meta Pixel already on this site is untouched — nothing here talks to `fbq`.
 */
(function () {
  "use strict";

  var GA4_MEASUREMENT_ID = "G-YVJV4S0ER4";
  var STORAGE_KEY = "ug_attribution_v1";
  var ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

  // ---- Attribution (kept byte-for-byte equivalent to src/lib/attribution.ts) ----------------
  function isExternalReferrer(referrer) {
    if (!referrer) return false;
    try {
      return new URL(referrer).hostname !== window.location.hostname;
    } catch (e) {
      return false;
    }
  }

  function buildTouch() {
    var params = new URLSearchParams(window.location.search);
    var utmSource = params.get("utm_source") || undefined;
    var referrer = document.referrer || undefined;
    var external = isExternalReferrer(referrer || "");
    return {
      source: utmSource || (external ? new URL(referrer).hostname : "(direct)"),
      medium: params.get("utm_medium") || (external ? "referral" : "(none)"),
      campaign: params.get("utm_campaign") || undefined,
      content: params.get("utm_content") || undefined,
      term: params.get("utm_term") || undefined,
      landingPage: window.location.pathname + window.location.search,
      referrer: referrer,
      timestamp: Date.now(),
    };
  }

  function hasNewAcquisitionSignal() {
    var params = new URLSearchParams(window.location.search);
    var hasUtm = UTM_KEYS.some(function (key) {
      return params.get(key);
    });
    return hasUtm || isExternalReferrer(document.referrer || "");
  }

  function readAttribution() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.firstTouch || !parsed.firstTouch.timestamp) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeAttribution(record) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {
      /* best-effort */
    }
  }

  function captureAttribution() {
    try {
      var stored = readAttribution();
      var expired = stored ? Date.now() - stored.firstTouch.timestamp > ATTRIBUTION_WINDOW_MS : true;
      if (!stored || expired) {
        var touch = buildTouch();
        writeAttribution({ firstTouch: touch, latestTouch: touch });
        return;
      }
      if (hasNewAcquisitionSignal()) {
        writeAttribution({ firstTouch: stored.firstTouch, latestTouch: buildTouch() });
      }
    } catch (e) {
      /* never break the page over attribution bookkeeping */
    }
  }

  // ---- GA4 ------------------------------------------------------------------------------------
  function loadGa4() {
    try {
      if (document.getElementById("ga4-gtag-js")) return;
      var script = document.createElement("script");
      script.id = "ga4-gtag-js";
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(GA4_MEASUREMENT_ID);
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag("js", new Date());
      window.gtag("config", GA4_MEASUREMENT_ID);
    } catch (e) {
      /* never break the page over analytics */
    }
  }

  // ---- design_cta_click -------------------------------------------------------------------------
  // Delegated on document.body rather than one listener per link, so this keeps working for any
  // "Design Your Hat"/"Start Designing"-style link added later without a code change. Matches on
  // the destination (the configurator entry path), not on link text, so it doesn't care what copy
  // a given button uses.
  function bindCtaTracking() {
    document.body.addEventListener(
      "click",
      function (event) {
        var link = event.target && event.target.closest ? event.target.closest('a[href^="/a/design"], a[href="/a/design"]') : null;
        if (!link) return;
        try {
          if (typeof window.gtag === "function") {
            window.gtag("event", "design_cta_click", { link_text: (link.textContent || "").trim().slice(0, 60) });
          }
        } catch (e) {
          /* never break navigation over a tracking call */
        }
      },
      { capture: true }
    );
  }

  captureAttribution();
  loadGa4();
  if (document.body) bindCtaTracking();
  else document.addEventListener("DOMContentLoaded", bindCtaTracking);
})();
